import { useRef, useCallback } from "react";
import { AuthenticationResult, AccountInfo } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";

type CachedToken = {
  token: string;
  expiresOn: number;
};

// Buffer time before token expiry to refresh early (5 minutes)
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

// Global state to prevent multiple popup instances across all hook instances
let globalPendingTokenRequest: Promise<string> | null = null;
let globalCachedToken: CachedToken | null = null;

// Track login attempts and cancellations
let loginCancelledTimestamp: number | null = null;
let loginAttemptCount = 0;
const MAX_LOGIN_ATTEMPTS = 3;
const LOGIN_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes cooldown after cancel/multiple failures

// LocalStorage key for permanently disabling MSAL
const MSAL_DISABLED_KEY = "msalDisabled";

// Check if user has permanently disabled MSAL
function isMsalDisabled(): boolean {
  return localStorage.getItem(MSAL_DISABLED_KEY) === "true";
}

// Permanently disable MSAL
export function disableMsal() {
  localStorage.setItem(MSAL_DISABLED_KEY, "true");
  console.log("MSAL login permanently disabled for this browser");
}

// Re-enable MSAL
export function enableMsal() {
  localStorage.removeItem(MSAL_DISABLED_KEY);
  resetLoginCooldown();
  console.log("MSAL login re-enabled");
}

// Type guard to check if error is a popup-blocked error
function isPopupBlockedError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const err = error as Record<string, unknown>;
  return (
    err.errorCode === "popup_window_error" ||
    (typeof err.errorMessage === "string" &&
      err.errorMessage.includes("popup")) ||
    err.name === "BrowserAuthError"
  );
}

// Type guard to check if user cancelled the login
function isUserCancelledError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const err = error as Record<string, unknown>;
  return (
    err.errorCode === "user_cancelled" ||
    (typeof err.errorMessage === "string" &&
      (err.errorMessage.includes("cancelled") ||
        err.errorMessage.includes("AADB2C90091") || // User cancelled flow
        err.errorMessage.includes("User closed the window")))
  );
}

// Check if we're in cooldown period
function isInCooldown(): boolean {
  if (!loginCancelledTimestamp) return false;
  const timeSinceCancel = Date.now() - loginCancelledTimestamp;
  return timeSinceCancel < LOGIN_COOLDOWN_MS;
}

// Reset cooldown (can be called manually by user action)
export function resetLoginCooldown() {
  loginCancelledTimestamp = null;
  loginAttemptCount = 0;
}

export function useAuth() {
  const { instance, accounts } = useMsal();
  const isMsalConnected = accounts.length > 0;

  const acquireAndCacheToken = useCallback(
    async (account: AccountInfo | null): Promise<string> => {
      if (!account) {
        throw new Error("Kan ikke hente token uten en konto");
      }

      try {
        const response: AuthenticationResult =
          await instance.acquireTokenSilent({
            ...loginRequest,
            account,
          });

        globalCachedToken = {
          token: response.accessToken,
          expiresOn:
            response.expiresOn?.getTime() ?? Date.now() + 5 * 60 * 1000,
        };

        return response.accessToken;
      } catch (error) {
        console.error("Failed to acquire token silently:", error);
        throw error;
      }
    },
    [instance]
  );

  const ensureAccount = useCallback(async (): Promise<AccountInfo | null> => {
    try {
      const response: AuthenticationResult = await instance.ssoSilent(
        loginRequest
      );
      return response.account ?? null;
    } catch (error) {
      console.warn("SSO silent login failed:", error);
      return null;
    }
  }, [instance]);

  const getToken = useCallback(async (): Promise<string> => {
    // Check if user has permanently disabled MSAL
    if (isMsalDisabled()) {
      throw new Error(
        "Microsoft login is disabled. Enable it in settings if you need access to calendar features."
      );
    }

    // Check if user has cancelled login recently
    if (isInCooldown()) {
      const remainingTime = Math.ceil(
        (LOGIN_COOLDOWN_MS - (Date.now() - (loginCancelledTimestamp ?? 0))) /
          1000 /
          60
      );
      throw new Error(
        `Login cancelled. Please wait ${remainingTime} minutes before trying again, or refresh the page to retry immediately.`
      );
    }

    // Return pending request if one exists (prevents multiple popups)
    if (globalPendingTokenRequest) {
      console.log("Reusing existing token request...");
      return globalPendingTokenRequest;
    }

    // Check if cached token is still valid (with buffer time)
    if (
      globalCachedToken &&
      Date.now() < globalCachedToken.expiresOn - TOKEN_REFRESH_BUFFER_MS
    ) {
      return globalCachedToken.token;
    }

    // Create new token request
    const tokenRequest = (async () => {
      try {
        const account: AccountInfo | null =
          accounts[0] ?? (await ensureAccount());

        if (!account) {
          console.log("No account found, initiating login...");

          // Check if too many failed attempts
          if (loginAttemptCount >= MAX_LOGIN_ATTEMPTS) {
            loginCancelledTimestamp = Date.now();
            throw new Error(
              `Too many login attempts. Please wait ${Math.ceil(
                LOGIN_COOLDOWN_MS / 1000 / 60
              )} minutes before trying again.`
            );
          }

          loginAttemptCount++;

          try {
            // Try popup first
            const loginResponse: AuthenticationResult =
              await instance.loginPopup(loginRequest);

            if (!loginResponse.account) {
              throw new Error("Login succeeded but no account returned");
            }

            // Reset attempt count on success
            loginAttemptCount = 0;
            return await acquireAndCacheToken(loginResponse.account);
          } catch (popupError: unknown) {
            // Check if user cancelled
            if (isUserCancelledError(popupError)) {
              console.log("User cancelled login");
              loginCancelledTimestamp = Date.now();

              throw new Error("Login cancelled. Please try again later.");
            }

            // Check if popup was blocked
            if (isPopupBlockedError(popupError)) {
              console.warn("Popup blocked. Cannot fall back to redirect.");
              throw new Error(
                "Innloggingspopup ble blokkert av nettleseren. Vennligst tillat popups for denne siden og prøv igjen."
              );
            }
            throw popupError;
          }
        }

        return await acquireAndCacheToken(account);
      } catch (err) {
        console.warn("Silent token acquisition failed, trying login:", err);

        try {
          const loginResponse: AuthenticationResult = await instance.loginPopup(
            loginRequest
          );

          if (!loginResponse.account) {
            throw new Error("Popup login succeeded but no account returned");
          }

          // Reset attempt count on success
          loginAttemptCount = 0;
          return await acquireAndCacheToken(loginResponse.account);
        } catch (popupError: unknown) {
          console.error("Popup login failed:", popupError);

          // Check if user cancelled
          if (isUserCancelledError(popupError)) {
            console.log("User cancelled login");
            loginCancelledTimestamp = Date.now();

            throw new Error("Login cancelled. Please try again later.");
          }

          // Check if popup was blocked
          if (isPopupBlockedError(popupError)) {
            console.warn("Popup blocked. Cannot fall back to redirect.");
            throw new Error(
              "Innloggingspopup ble blokkert av nettleseren. Vennligst tillat popups for denne siden og prøv igjen."
            );
          }

          // Clear cache on auth failure to force re-auth next time
          globalCachedToken = null;

          throw new Error(
            `Authentication failed: ${
              popupError instanceof Error ? popupError.message : "Unknown error"
            }`
          );
        }
      } finally {
        // Clear the pending request when done
        globalPendingTokenRequest = null;
      }
    })();

    globalPendingTokenRequest = tokenRequest;
    return tokenRequest;
  }, [accounts, acquireAndCacheToken, ensureAccount, instance]);

  return {
    getToken,
    isMsalConnected,
    isInCooldown: isInCooldown(),
    isMsalDisabled: isMsalDisabled(),
    resetCooldown: resetLoginCooldown,
    disableMsal,
    enableMsal,
  };
}
